/**
 * Bundle Builder Custom Element
 * Manages product selection, slots, progress bar, and cart integration
 * Persists selection across page navigation using localStorage
 * Supports editing existing bundles from cart
 */
class BundleBuilder extends HTMLElement {
  constructor() {
    super();
    
    this.slots = [];
    this.selectedSlotIndex = null;
    this.tiers = [];
    this.currency = '€';
    this.defaultSlots = 7;
    this.enableUnlimited = true;
    this.slotMode = 'separate';
    this.storageKey = 'bundle_builder_selection';
    
    // Edit mode properties
    this.editMode = false;
    this.editBundleId = null;
    
    this.miniCart = document.querySelector('mini-cart');
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.defaultSlots = parseInt(this.dataset.defaultSlots) || 7;
    this.enableUnlimited = this.dataset.enableUnlimited === 'true';
    this.slotMode = this.dataset.slotMode || 'separate';
    this.currency = this.dataset.currency || '€';
    this.sectionId = this.dataset.sectionId || 'default';
    
    // Create unique storage key per collection/section
    this.storageKey = `bundle_builder_${this.sectionId}`;
    
    // Check for edit mode from URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    this.editBundleId = urlParams.get('edit_bundle');
    this.editMode = !!this.editBundleId;
    
    try {
      let tiersData = this.dataset.tiers || '[]';
      const textarea = document.createElement('textarea');
      textarea.innerHTML = tiersData;
      tiersData = textarea.value;
      
      this.tiers = JSON.parse(tiersData);
    } catch (e) {
      console.error('Bundle Builder: Failed to parse tiers', e);
      this.tiers = [];
    }

    this.slotsContainer = this.querySelector('[data-slots-container]');
    const slotWrappers = this.querySelectorAll('.bundle-builder__slot-wrapper');
    slotWrappers.forEach((wrapper, index) => {
      const slotElement = wrapper.querySelector('.bundle-builder__slot');
      this.slots.push({
        element: slotElement,
        wrapper: wrapper,
        filled: false,
        product: null
      });
    });

    this.progressFill = this.querySelector('.bundle-builder__progress-fill');
    this.tierElements = this.querySelectorAll('.bundle-builder__tier');
    
    const footer = this.querySelector('.bundle-builder__footer');
    this.minSetsForDiscount = parseInt(footer?.dataset.minSets) || 4;
    this.footerInactive = this.querySelector('[data-footer-inactive]');
    this.footerActive = this.querySelector('[data-footer-active]');
    this.footerMessage = this.querySelector('[data-footer-message]');
    this.footerSavings = this.querySelector('[data-footer-savings]');
    this.footerOriginal = this.querySelector('[data-footer-original]');
    this.footerTotal = this.querySelector('[data-footer-total]');
    this.addToCartButton = this.querySelector('[data-add-bundle-to-cart]');

    this.stickyHeader = this.querySelector('[data-sticky-header]');
    this.initStickyHeader();

    this.bindEvents();
    
    // If in edit mode, load bundle from cart instead of localStorage
    if (this.editMode) {
      this.clearStorage();
      this.loadBundleForEdit();
    } else {
      this.restoreFromStorage();
    }
    
    this.ensureEmptySlotVisible();
    
    this.updateUI();
    if (this.editMode && this.addToCartButton) {
      this.addToCartButton.textContent = this.getTranslation('footer.update_bundle');
    }

    // Add AH
    const stickyFilters = document.querySelector('sticky-facet-filters');
if (stickyFilters) {
  const observer = new MutationObserver(() => {
    const footer = this.querySelector('.bundle-builder__footer');
    if (footer) {
      const isSticky = stickyFilters.classList.contains('shopify-section-filters-sticky');
      footer.classList.toggle('is-top', !isSticky);
    }
  });
  observer.observe(stickyFilters, { attributes: true, attributeFilter: ['class'] });
  
  // État initial
  const footer = this.querySelector('.bundle-builder__footer');
  if (footer) {
    const isSticky = stickyFilters.classList.contains('shopify-section-filters-sticky');
    footer.classList.toggle('is-top', !isSticky);
  }
}

  }

  // ============================================
  // Edit Mode - Load Bundle from Cart
  // ============================================
  
  async loadBundleForEdit() {
    if (!this.editBundleId) return;
    
    try {
      const response = await fetch(`${theme.routes.cart_url}.js`);
      const cart = await response.json();

      const bundleItems = cart.items.filter(item => 
        item.properties && item.properties['_bundle_id'] === this.editBundleId
      );
      
      bundleItems.sort((a, b) => {
        const posA = parseInt(a.properties?.['_bundle_position'] || '0', 10);
        const posB = parseInt(b.properties?.['_bundle_position'] || '0', 10);
        return posA - posB;
      });
      
      if (bundleItems.length === 0) {
        console.warn('Bundle Builder: No items found for bundle ID', this.editBundleId);
        this.editMode = false;
        this.editBundleId = null;
        this.restoreFromStorage();
        return;
      }
      
      this.clearSlotsOnly();
      
      bundleItems.forEach(item => {
        const card = this.findCardByProductId(item.product_id.toString());
        
        const productData = {
          productId: item.product_id.toString(),
          variantId: item.variant_id.toString(),
          title: item.product_title,
          price: item.price,
          image: item.image,
          quantity: item.quantity,
          cardElement: card || null
        };
        
        this.restoreProductToSlot(productData);
        
        if (card) {
          this.setCardAsSelected(card, item.quantity);
        }
      });
      
      this.ensureEmptySlotVisible();
      this.updateUI();
      
    } catch (e) {
      console.error('Bundle Builder: Failed to load bundle for edit', e);
      this.editMode = false;
      this.editBundleId = null;
      this.restoreFromStorage();
    }
  }
  
  clearSlotsOnly() {
    this.slots.forEach((slot, index) => {
      if (slot.filled && slot.product?.cardElement) {
        this.resetCardUI(slot.product.cardElement);
      }
      slot.filled = false;
      slot.product = null;
      this.updateSlotUI(index);
    });
    
    while (this.slots.length > this.defaultSlots) {
      const slot = this.slots.pop();
      if (slot.wrapper) {
        slot.wrapper.remove();
      } else {
        slot.element.remove();
      }
    }
    
    this.selectedSlotIndex = null;
  }

  generateBundleId() {
    return `bundle_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  async removeOldBundleItems() {
    if (!this.editBundleId) return;
    
    try {
      const response = await fetch(`${theme.routes.cart_url}.js`);
      const cart = await response.json();
      
      const linesToRemove = [];
      cart.items.forEach((item, index) => {
        if (item.properties && item.properties['_bundle_id'] === this.editBundleId) {
          linesToRemove.push(index + 1);
        }
      });
      
      if (linesToRemove.length === 0) return;
      
      linesToRemove.sort((a, b) => b - a);
      
      for (const line of linesToRemove) {
        await fetch(`${theme.routes.cart_change_url}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            line: line,
            quantity: 0
          })
        });
      }
      
    } catch (e) {
      console.error('Bundle Builder: Failed to remove old bundle items', e);
    }
  }

  // ============================================
  // LocalStorage Persistence
  // ============================================
  
  saveToStorage() {
    try {
      const savedProducts = [];
      this.slots.forEach(slot => {
        if (slot.filled && slot.product) {
          savedProducts.push({
            productId: slot.product.productId,
            variantId: slot.product.variantId,
            title: slot.product.title,
            price: slot.product.price,
            image: slot.product.image,
            quantity: slot.product.quantity
          });
        }
      });
      
      localStorage.setItem(this.storageKey, JSON.stringify({
        products: savedProducts,
        timestamp: Date.now()
      }));
    } catch (e) {
      console.warn('Bundle Builder: Failed to save to localStorage', e);
    }
  }

  restoreFromStorage() {
    try {
      const saved = localStorage.getItem(this.storageKey);
      if (!saved) return;
      
      const data = JSON.parse(saved);
      
      const maxAge = 24 * 60 * 60 * 1000;
      if (Date.now() - data.timestamp > maxAge) {
        localStorage.removeItem(this.storageKey);
        return;
      }
      
      if (!data.products || data.products.length === 0) return;
      
      data.products.forEach(savedProduct => {
        const card = this.findCardByProductId(savedProduct.productId);
        
        const productData = {
          productId: savedProduct.productId,
          variantId: savedProduct.variantId,
          title: savedProduct.title,
          price: savedProduct.price,
          image: savedProduct.image,
          quantity: savedProduct.quantity,
          cardElement: card || null
        };
        
        this.restoreProductToSlot(productData);
        
        if (card) {
          this.setCardAsSelected(card, savedProduct.quantity);
        }
      });
      
    } catch (e) {
      console.warn('Bundle Builder: Failed to restore from localStorage', e);
    }
  }

  restoreProductToSlot(productData) {
    if (this.slotMode === 'separate') {
      for (let i = 0; i < productData.quantity; i++) {
        let targetIndex = this.slots.findIndex(slot => !slot.filled);
        
        if (targetIndex === -1 && this.enableUnlimited) {
          targetIndex = this.addNewSlot();
        }
        
        if (targetIndex === -1) break;
        
        const slot = this.slots[targetIndex];
        slot.filled = true;
        slot.product = {
          ...productData,
          quantity: 1
        };
        this.updateSlotUI(targetIndex);
      }
    } else {
      let targetIndex = this.slots.findIndex(slot => !slot.filled);
      
      if (targetIndex === -1 && this.enableUnlimited) {
        targetIndex = this.addNewSlot();
      }
      
      if (targetIndex === -1) return;
      
      const slot = this.slots[targetIndex];
      slot.filled = true;
      slot.product = productData;
      this.updateSlotUI(targetIndex);
    }
  }

  findCardByProductId(productId) {
    return this.querySelector(`[data-product-id="${productId}"]`);
  }

  setCardAsSelected(card, quantity) {
    const addBtn = card.querySelector('[data-bundle-add]');
    const qtySelector = card.querySelector('[data-bundle-quantity], [data-quantity-selector]');
    const qtyInput = card.querySelector('[data-bundle-qty-input], [data-qty-input]');

    if (addBtn) addBtn.style.display = 'none';
    if (qtySelector) qtySelector.style.display = 'flex';
    if (qtyInput) qtyInput.value = quantity;

    card.setAttribute('data-bundle-selected', 'true');
  }

  clearStorage() {
    try {
      localStorage.removeItem(this.storageKey);
    } catch (e) {
      console.warn('Bundle Builder: Failed to clear localStorage', e);
    }
  }

  // ============================================
  // Ensure Empty Slot Visible (Unlimited Mode)
  // ============================================
  
  ensureEmptySlotVisible() {
    if (!this.enableUnlimited) return;
    
    const hasEmptySlot = this.slots.some(slot => !slot.filled);
    
    if (!hasEmptySlot) {
      this.addNewSlot();
    }
  }

  // ============================================
  // Sticky Header & Height Management
  // ============================================

  initStickyHeader() {
    if (!this.stickyHeader) return;

    const sentinel = document.createElement('div');
    sentinel.className = 'bundle-builder__sticky-sentinel';
    sentinel.style.cssText = 'position: absolute; top: 0; left: 0; right: 0; height: 1px; pointer-events: none;';
    this.stickyHeader.parentNode.insertBefore(sentinel, this.stickyHeader);

    this.shopifyHeader = document.querySelector('.shopify-section-header');
    
    const updateHeaderStickyState = () => {
      if (!this.shopifyHeader) return;
      
      const isHeaderSticky = this.shopifyHeader.classList.contains('shopify-section-header-sticky') &&
                             !this.shopifyHeader.classList.contains('shopify-section-header-hidden');
      
      if (isHeaderSticky) {
        this.stickyHeader.classList.add('header-is-sticky');
      } else {
        this.stickyHeader.classList.remove('header-is-sticky');
      }
    };

    const updateBundleStickyState = () => {
      const headerOffset = this.stickyHeader.classList.contains('header-is-sticky') 
        ? (window.innerWidth >= 750 ? 70 : 56)
        : 0;
      
      const rect = sentinel.getBoundingClientRect();
      
      if (rect.top <= headerOffset) {
        this.stickyHeader.classList.add('is-stuck');
      } else {
        this.stickyHeader.classList.remove('is-stuck');
      }
    };

    if (this.shopifyHeader) {
      const headerObserver = new MutationObserver(() => {
        updateHeaderStickyState();
        updateBundleStickyState();
      });
      headerObserver.observe(this.shopifyHeader, { attributes: true, attributeFilter: ['class'] });
    }
    
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          updateHeaderStickyState();
          updateBundleStickyState();
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });

    updateHeaderStickyState();
    updateBundleStickyState();
    
    this.setBundleStickyHeight();
  }

  setBundleStickyHeight() {
    if (!this.stickyHeader) return;
    
    const updateHeight = () => {
      const height = this.stickyHeader.offsetHeight;
      document.documentElement.style.setProperty('--bundle-sticky-height', `${height}px`);
    };
    
    updateHeight();
    
    window.addEventListener('resize', updateHeight);
    
    const observer = new MutationObserver(updateHeight);
    observer.observe(this.stickyHeader, { childList: true, subtree: true });
  }

  // ============================================
  // Event Binding
  // ============================================

 bindEvents() {
  // Slots - délégation sur le container
  this.slots.forEach((slot, index) => {
    slot.element.addEventListener('click', (e) => {
      if (!e.target.closest('.bundle-builder__slot-remove')) {
        this.selectSlot(index);
      }
    });

    const removeBtn = slot.element.querySelector('.bundle-builder__slot-remove');
    if (removeBtn) {
      removeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.removeProduct(index);
      });
    }
  });

  // Délégation d'événements pour les cartes produits (fonctionne après filtrage)
  this.addEventListener('click', (e) => {
    const card = e.target.closest('[data-bundle-product], .bundle-card');
    if (!card) return;

    // Bouton Ajouter
    const addBtn = e.target.closest('[data-bundle-add]');
    if (addBtn) {
      this.handleAddClick(card);
      return;
    }

    // Bouton Moins
    const qtyMinus = e.target.closest('[data-bundle-qty-minus], [data-qty-minus]');
    if (qtyMinus) {
      this.handleQuantityChange(card, -1);
      return;
    }

    // Bouton Plus
    const qtyPlus = e.target.closest('[data-bundle-qty-plus], [data-qty-plus]');
    if (qtyPlus) {
      this.handleQuantityChange(card, 1);
      return;
    }
  });

  // Délégation pour l'input de quantité (change event)
  this.addEventListener('change', (e) => {
    const qtyInput = e.target.closest('[data-bundle-qty-input], [data-qty-input]');
    if (qtyInput) {
      const card = qtyInput.closest('[data-bundle-product], .bundle-card');
      if (card) {
        this.handleQuantityInput(card);
      }
    }
  });

  if (this.addToCartButton) {
    this.addToCartButton.addEventListener('click', () => this.addBundleToCart());
  }

  this.setupDragScroll();
  
  document.addEventListener('bundle:addFromQuickView', (e) => {
    this.handleQuickViewAdd(e.detail);
  });
}

  setupDragScroll() {
    const wrapper = this.querySelector('.bundle-builder__slots-wrapper');
    const slots = this.querySelector('.bundle-builder__slots');
    if (!wrapper || !slots) return;

    let isDown = false;
    let startX;
    let scrollLeft;

    const checkScrollable = () => {
      if (slots.scrollWidth > wrapper.clientWidth) {
        wrapper.classList.add('bundle-builder__slots-wrapper--scrollable');
      } else {
        wrapper.classList.remove('bundle-builder__slots-wrapper--scrollable');
      }
    };

    checkScrollable();
    window.addEventListener('resize', checkScrollable);
    
    this.checkScrollable = checkScrollable;

    wrapper.addEventListener('mousedown', (e) => {
      if (e.target.closest('button') || e.target.closest('.bundle-builder__slot-remove')) return;
      
      isDown = true;
      wrapper.style.scrollBehavior = 'auto';
      startX = e.pageX - wrapper.offsetLeft;
      scrollLeft = wrapper.scrollLeft;
    });

    wrapper.addEventListener('mouseleave', () => {
      isDown = false;
      wrapper.style.scrollBehavior = '';
    });

    wrapper.addEventListener('mouseup', () => {
      isDown = false;
      wrapper.style.scrollBehavior = '';
    });

    wrapper.addEventListener('mousemove', (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - wrapper.offsetLeft;
      const walk = (x - startX) * 1.5;
      wrapper.scrollLeft = scrollLeft - walk;
    });
  }

  // ============================================
  // Quick View Integration
  // ============================================

  handleQuickViewAdd(detail) {
    const productData = {
      productId: detail.productId,
      variantId: detail.variantId,
      title: detail.title,
      price: detail.price,
      image: detail.image,
      quantity: detail.quantity || 1,
      cardElement: null
    };
    
    this.addProduct(productData);
  }

  // ============================================
  // Add/Remove Product Handlers
  // ============================================

  handleAddClick(card) {
    const addBtn = card.querySelector('[data-bundle-add]');
    const qtySelector = card.querySelector('[data-bundle-quantity], [data-quantity-selector]');
    const qtyInput = card.querySelector('[data-bundle-qty-input], [data-qty-input]');

    if (addBtn) addBtn.style.display = 'none';
    if (qtySelector) qtySelector.style.display = 'flex';
    if (qtyInput) qtyInput.value = 1;

    card.setAttribute('data-bundle-selected', 'true');

    this.addProductFromCard(card, 1);
  }

  handleQuantityChange(card, delta) {
    const qtyInput = card.querySelector('[data-bundle-qty-input], [data-qty-input]');
    if (!qtyInput) return;

    let newQty = parseInt(qtyInput.value) + delta;
    if (newQty < 0) newQty = 0;
    if (newQty > 99) newQty = 99;

    qtyInput.value = newQty;

    if (newQty === 0) {
      this.removeProductByCard(card);
      this.resetCardUI(card);
    } else {
      this.updateProductQuantity(card, newQty);
    }
  }

  handleQuantityInput(card) {
    const qtyInput = card.querySelector('[data-bundle-qty-input], [data-qty-input]');
    if (!qtyInput) return;

    let qty = parseInt(qtyInput.value) || 0;
    if (qty < 0) qty = 0;
    if (qty > 99) qty = 99;
    qtyInput.value = qty;

    if (qty === 0) {
      this.removeProductByCard(card);
      this.resetCardUI(card);
    } else {
      this.updateProductQuantity(card, qty);
    }
  }

  resetCardUI(card) {
    const addBtn = card.querySelector('[data-bundle-add]');
    const qtySelector = card.querySelector('[data-bundle-quantity], [data-quantity-selector]');
    const qtyInput = card.querySelector('[data-bundle-qty-input], [data-qty-input]');

    if (addBtn) addBtn.style.display = '';
    if (qtySelector) qtySelector.style.display = 'none';
    if (qtyInput) qtyInput.value = 1;
    
    card.removeAttribute('data-bundle-selected');
  }

  addProductFromCard(card, quantity) {
    const productData = {
      productId: card.dataset.productId,
      variantId: card.dataset.variantId,
      title: card.dataset.productTitle,
      price: parseInt(card.dataset.productPrice),
      image: card.dataset.productImage,
      quantity: quantity,
      cardElement: card
    };

    this.addProduct(productData);
  }

  addProduct(productData) {
    let result;
    if (this.slotMode === 'separate') {
      result = this.addProductSeparate(productData);
    } else {
      result = this.addProductCombine(productData);
    }
    
    // Save to storage after adding
    this.saveToStorage();
    
    // Ensure empty slot visible in unlimited mode
    this.ensureEmptySlotVisible();
    
    return result;
  }

  addProductCombine(productData) {
    let targetIndex = this.selectedSlotIndex;
    
    if (targetIndex === null || this.slots[targetIndex]?.filled) {
      targetIndex = this.slots.findIndex(slot => !slot.filled);
    }

    if (targetIndex === -1 && this.enableUnlimited) {
      targetIndex = this.addNewSlot();
    }

    if (targetIndex === -1) {
      console.warn('Bundle Builder: No available slots');
      return false;
    }

    const slot = this.slots[targetIndex];
    slot.filled = true;
    slot.product = productData;

    this.updateSlotUI(targetIndex);
    
    this.selectedSlotIndex = null;
    this.updateUI();

    return true;
  }

  addProductSeparate(productData) {
    const slotsToFill = productData.quantity;
    
    const existingSlots = this.slots.filter(
      slot => slot.filled && slot.product?.productId === productData.productId
    );
    const existingCount = existingSlots.length;
    
    const slotsNeeded = slotsToFill - existingCount;
    
    if (slotsNeeded > 0) {
      for (let i = 0; i < slotsNeeded; i++) {
        let targetIndex = this.slots.findIndex(slot => !slot.filled);
        
        if (targetIndex === -1 && this.enableUnlimited) {
          targetIndex = this.addNewSlot();
        }
        
        if (targetIndex === -1) {
          console.warn('Bundle Builder: No available slots');
          break;
        }
        
        const slot = this.slots[targetIndex];
        slot.filled = true;
        slot.product = {
          ...productData,
          quantity: 1
        };
        this.updateSlotUI(targetIndex);
      }
    } else if (slotsNeeded < 0) {
      const slotsToRemove = Math.abs(slotsNeeded);
      let removed = 0;
      
      for (let i = this.slots.length - 1; i >= 0 && removed < slotsToRemove; i--) {
        const slot = this.slots[i];
        if (slot.filled && slot.product?.productId === productData.productId) {
          slot.filled = false;
          slot.product = null;
          this.updateSlotUI(i);
          removed++;
        }
      }
      
      this.cleanupEmptySlots();
    }
    
    this.selectedSlotIndex = null;
    this.updateUI();
    return true;
  }

  updateProductQuantity(card, newQuantity) {
    if (this.slotMode === 'separate') {
      const productData = {
        productId: card.dataset.productId,
        variantId: card.dataset.variantId,
        title: card.dataset.productTitle,
        price: parseInt(card.dataset.productPrice),
        image: card.dataset.productImage,
        quantity: newQuantity,
        cardElement: card
      };
      this.addProductSeparate(productData);
    } else {
      const slotIndex = this.slots.findIndex(
        slot => slot.filled && slot.product?.cardElement === card
      );

      if (slotIndex !== -1) {
        this.slots[slotIndex].product.quantity = newQuantity;
        this.updateSlotUI(slotIndex);
        this.updateUI();
      }
    }
    
    this.saveToStorage();
    
    this.ensureEmptySlotVisible();
  }

  removeProduct(slotIndex) {
    const slot = this.slots[slotIndex];
    if (!slot || !slot.filled) return;

    const card = slot.product?.cardElement;
    const productId = slot.product?.productId;

    if (this.slotMode === 'separate' && card) {
      const qtyInput = card.querySelector('[data-bundle-qty-input], [data-qty-input]');
      if (qtyInput) {
        const remainingCount = this.slots.filter(
          (s, i) => i !== slotIndex && s.filled && s.product?.productId === productId
        ).length;
        
        if (remainingCount === 0) {
          this.resetCardUI(card);
        } else {
          qtyInput.value = remainingCount;
        }
      }
    } else if (this.slotMode === 'separate' && !card) {
    } else if (card) {
      this.resetCardUI(card);
    }

    slot.filled = false;
    slot.product = null;

    this.updateSlotUI(slotIndex);
    
    // Compact slots to fill gaps
    this.compactSlots();
    
    this.cleanupEmptySlots();
    
    this.ensureEmptySlotVisible();
    
    this.updateUI();
    
    this.saveToStorage();
  }

  cleanupEmptySlots() {
    const minSlots = this.enableUnlimited ? this.defaultSlots : this.defaultSlots;
    
    while (this.slots.length > minSlots) {
      const lastSlot = this.slots[this.slots.length - 1];
      
      if (!lastSlot.filled) {
        const emptyCount = this.slots.filter(s => !s.filled).length;
        if (emptyCount > 1 || !this.enableUnlimited) {
          // Remove the wrapper element (which contains the slot)
          if (lastSlot.wrapper) {
            lastSlot.wrapper.remove();
          } else {
            lastSlot.element.remove();
          }
          this.slots.pop();
        } else {
          break;
        }
      } else {
        break;
      }
    }
  }

  // Compact slots - shift all products to fill gaps
  compactSlots() {
    // Collect all filled products
    const filledProducts = [];
    this.slots.forEach(slot => {
      if (slot.filled && slot.product) {
        filledProducts.push({ ...slot.product });
      }
    });
    
    // Clear all slots
    this.slots.forEach((slot, index) => {
      slot.filled = false;
      slot.product = null;
      this.updateSlotUI(index);
    });
    
    // Re-add products in order
    filledProducts.forEach((product, index) => {
      if (index < this.slots.length) {
        this.slots[index].filled = true;
        this.slots[index].product = product;
        this.updateSlotUI(index);
      }
    });
  }

  removeProductByCard(card) {
    if (this.slotMode === 'separate') { 
      const productId = card.dataset.productId;
      this.slots.forEach((slot, index) => {
        if (slot.filled && slot.product?.productId === productId) {
          slot.filled = false;
          slot.product = null;
          this.updateSlotUI(index);
        }
      });
      this.compactSlots();
      this.cleanupEmptySlots();
      this.ensureEmptySlotVisible();
      this.updateUI();
      this.saveToStorage();
    } else {
      const slotIndex = this.slots.findIndex(
        slot => slot.filled && slot.product?.cardElement === card
      );

      if (slotIndex !== -1) {
        this.slots[slotIndex].filled = false;
        this.slots[slotIndex].product = null;
        this.updateSlotUI(slotIndex);
        this.compactSlots();
        this.cleanupEmptySlots();
        this.ensureEmptySlotVisible();
        this.updateUI();
        this.saveToStorage();
      }
    }
  }

  selectSlot(index) {
    if (this.selectedSlotIndex !== null) {
      this.slots[this.selectedSlotIndex].element.classList.remove('bundle-builder__slot--selected');
    }

    if (this.selectedSlotIndex === index) {
      this.selectedSlotIndex = null;
    } else {
      this.selectedSlotIndex = index;
      this.slots[index].element.classList.add('bundle-builder__slot--selected');
    }
  }

  addNewSlot() {
    const newIndex = this.slots.length;
    
    const wrapperEl = document.createElement('div');
    wrapperEl.className = 'bundle-builder__slot-wrapper';
    wrapperEl.dataset.slotIndex = newIndex;
    wrapperEl.innerHTML = `
      <div class="bundle-builder__slot bundle-builder__slot--empty">
        <button type="button" class="bundle-builder__slot-remove" aria-label="Remove" style="display: none;">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
        <div class="bundle-builder__slot-content">
          <span class="bundle-builder__slot-plus">+</span>
        </div>
        <div class="bundle-builder__slot-product" style="display: none;">
          <img class="bundle-builder__slot-image" src="" alt="" width="88" height="106" loading="lazy">
          <span class="bundle-builder__slot-quantity"></span>
        </div>
      </div>
      <span class="bundle-builder__slot-title" style="display: none;"></span>
    `;

    this.slotsContainer.appendChild(wrapperEl);
    
    const slotEl = wrapperEl.querySelector('.bundle-builder__slot');

    const newSlot = {
      element: slotEl,
      wrapper: wrapperEl,
      filled: false,
      product: null
    };
    this.slots.push(newSlot);

    slotEl.addEventListener('click', (e) => {
      if (!e.target.closest('.bundle-builder__slot-remove')) {
        this.selectSlot(newIndex);
      }
    });

    const removeBtn = slotEl.querySelector('.bundle-builder__slot-remove');
    if (removeBtn) {
      removeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.removeProduct(newIndex);
      });
    }

    return newIndex;
  }

  updateSlotUI(index) {
    const slot = this.slots[index];
    if (!slot) return;

    const element = slot.element;
    const wrapper = slot.wrapper;
    const contentEl = element.querySelector('.bundle-builder__slot-content');
    const productEl = element.querySelector('.bundle-builder__slot-product');
    const removeBtn = element.querySelector('.bundle-builder__slot-remove');
    const imageEl = element.querySelector('.bundle-builder__slot-image');
    const qtyEl = element.querySelector('.bundle-builder__slot-quantity');
    const titleEl = wrapper?.querySelector('.bundle-builder__slot-title');

    if (slot.filled && slot.product) {
      element.classList.remove('bundle-builder__slot--empty');
      element.classList.add('bundle-builder__slot--filled');
      
      if (contentEl) contentEl.style.display = 'none';
      if (productEl) productEl.style.display = 'flex';
      if (removeBtn) removeBtn.style.display = '';
      
      if (imageEl) {
        imageEl.src = slot.product.image;
        imageEl.alt = slot.product.title;
      }
      if (qtyEl) {
        qtyEl.textContent = slot.product.quantity > 1 ? `×${slot.product.quantity}` : '';
      }
      if (titleEl) {
        titleEl.textContent = slot.product.title;
        titleEl.style.display = '';
      }
    } else {
      element.classList.add('bundle-builder__slot--empty');
      element.classList.remove('bundle-builder__slot--filled');
      
      if (contentEl) contentEl.style.display = '';
      if (productEl) productEl.style.display = 'none';
      if (removeBtn) removeBtn.style.display = 'none';
      if (titleEl) {
        titleEl.textContent = '';
        titleEl.style.display = 'none';
      }
    }
  }

  // ============================================
  // Calculations
  // ============================================

  getTotalSets() {
    return this.slots.reduce((total, slot) => {
      if (slot.filled && slot.product) {
        return total + slot.product.quantity;
      }
      return total;
    }, 0);
  }

  getCurrentTier() {
    const totalSets = this.getTotalSets();
    let currentTier = null;

    for (const tier of this.tiers) {
      if (totalSets >= tier.sets) {
        currentTier = tier;
      }
    }

    return currentTier;
  }

  getNextTier() {
    const totalSets = this.getTotalSets();
    
    for (const tier of this.tiers) {
      if (totalSets < tier.sets) {
        return tier;
      }
    }

    return null;
  }

  calculateTotal() {
    return this.slots.reduce((total, slot) => {
      if (slot.filled && slot.product) {
        return total + (slot.product.price * slot.product.quantity);
      }
      return total;
    }, 0);
  }

  calculateSavings() {
  const discountsByPosition = {
    2: 15,
    3: 20,
    4: 100,
    5: 25,
    6: 30,
    7: 35,
    8: 100,
    9: 40
  };

  const totalSets = this.getTotalSets();
  
  // Si moins de 2 articles, pas de réduction
  if (totalSets < 2) return 0;

  // Collecter tous les prix des produits (1 par position)
  const prices = [];
  this.slots.forEach(slot => {
    if (slot.filled && slot.product) {
      // En mode "separate", chaque slot = 1 quantité
      for (let i = 0; i < slot.product.quantity; i++) {
        prices.push(slot.product.price);
      }
    }
  });

  // Calculer les économies par position
  let savings = 0;
  prices.forEach((price, index) => {
    const position = index + 1;
    let discountPercent = 0;
    
    if (position >= 9) {
      discountPercent = 40;
    } else if (discountsByPosition[position]) {
      discountPercent = discountsByPosition[position];
    }
    
    savings += Math.round(price * (discountPercent / 100));
  });

  return savings;
}

  // ============================================
  // UI Updates
  // ============================================

  updateUI() {
    this.updateProgressBar();
    this.updateFooter();
    this.updateProductCardStates();
    
    if (this.checkScrollable) {
      this.checkScrollable();
    }
  }

  updateProductCardStates() {
    const filledSlots = this.slots.filter(slot => slot.filled).length;
    const limitReached = !this.enableUnlimited && filledSlots >= this.defaultSlots;
    
    this.querySelectorAll('[data-bundle-product], .bundle-card, .card-wrapper--bundle').forEach(card => {
      const isSelected = card.getAttribute('data-bundle-selected') === 'true';
      const addBtn = card.querySelector('[data-bundle-add]');
      const qtyPlus = card.querySelector('[data-bundle-qty-plus], [data-qty-plus]');
      
      if (limitReached) {
        if (addBtn && !isSelected) {
          addBtn.disabled = true;
          addBtn.classList.add('bundle-limit-reached');
        }
        
        if (qtyPlus && isSelected) {
          qtyPlus.disabled = true;
          qtyPlus.classList.add('bundle-limit-reached');
        }
      } else {
        if (addBtn) {
          addBtn.disabled = false;
          addBtn.classList.remove('bundle-limit-reached');
        }
        if (qtyPlus) {
          qtyPlus.disabled = false;
          qtyPlus.classList.remove('bundle-limit-reached');
        }
      }
    });
  }

  updateProgressBar() {
    const totalSets = this.getTotalSets();
    const tierCount = this.tiers.length;
    
    let progressPercent = 0;
    
    if (tierCount > 0 && totalSets > 0) {
      const maxSets = this.tiers[tierCount - 1].sets;
      
      progressPercent = Math.min((totalSets / maxSets) * 100, 100);
    }
    
    if (this.progressFill) {
      this.progressFill.style.width = `${progressPercent}%`;
    }

    this.tierElements.forEach(tierEl => {
      const tierSets = parseInt(tierEl.dataset.tierSets);
      
      tierEl.classList.remove('bundle-builder__tier--active', 'bundle-builder__tier--completed');
      
      if (totalSets >= tierSets) {
        tierEl.classList.add('bundle-builder__tier--completed');
      }
    });
  }

  updateFooter() {
    const totalSets = this.getTotalSets();
    const total = this.calculateTotal();
    const savings = this.calculateSavings();
    
    const isActive = totalSets >= this.minSetsForDiscount;
    
    if (isActive) {
      if (this.footerInactive) this.footerInactive.style.display = 'none';
      if (this.footerActive) this.footerActive.style.display = '';
      
      if (this.footerSavings) {
        if (savings > 0) {
          this.footerSavings.textContent = this.getTranslation('footer.savings')
            .replace('{{ amount }}', this.formatMoney(savings));
          this.footerSavings.style.display = '';
        } else {
          this.footerSavings.style.display = 'none';
        }
      }

      if (this.footerOriginal) {
        if (savings > 0) {
          this.footerOriginal.textContent = this.formatMoney(total);
          this.footerOriginal.style.display = '';
        } else {
          this.footerOriginal.style.display = 'none';
        }
      }

      if (this.footerTotal) {
        this.footerTotal.textContent = this.formatMoney(total - savings);
      }
    } else {
      if (this.footerInactive) this.footerInactive.style.display = '';
      if (this.footerActive) this.footerActive.style.display = 'none';
      
      if (this.footerMessage) {
        const setsNeeded = this.minSetsForDiscount - totalSets;
        this.footerMessage.textContent = this.getTranslation('footer.more_sets_needed')
          .replace('{{ count }}', setsNeeded);
      }
    }
  }

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2).replace('.', ',');
    return `${amount}${this.currency}`;
  }

  getTranslation(key) {
    const translations = {
      'footer.select_products': 'Sélectionner les produits',
      'footer.more_sets_needed': 'Plus que {{ count }} sets à choisir pour voir le prix',
      'footer.savings': 'Économies : {{ amount }}',
      'footer.update_bundle': 'Mettre à jour le bundle',
      'footer.edit_bundle': 'Modifier le bundle'
    };

    if (window.theme?.strings?.bundle_builder) {
      const keys = key.split('.');
      let value = window.theme.strings.bundle_builder;
      for (const k of keys) {
        value = value?.[k];
      }
      if (value) return value;
    }

    return translations[key] || key;
  }

  // ============================================
  // Cart Integration
  // ============================================

  async addBundleToCart() {
    const bundleId = this.generateBundleId();
    
    const items = [];
    let position = 1;
    
    this.slots.forEach(slot => {
      if (slot.filled && slot.product) {
        const qty = slot.product.quantity || 1;
        
        for (let i = 0; i < qty; i++) {
          items.push({
            id: parseInt(slot.product.variantId),
            quantity: 1,
            properties: {
              '_bundled-product': 'true',
              '_bundle_id': bundleId,
              '_bundle_position': position.toString()
            }
          });
          position++;
        }
      }
    });

    if (items.length === 0) return;

    this.addToCartButton.disabled = true;
    this.addToCartButton.classList.add('loading');

    try {
      if (this.editMode) {
        await this.removeOldBundleItems();
      }
      
      const sections = this.miniCart ? this.miniCart.getSectionsToRender().map(s => s.id) : [];
      
      // Reverse items so they appear in correct order in cart (Shopify adds last item first)
      const reversedItems = [...items].reverse();
      
      const response = await fetch(`${theme.routes.cart_add_url}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: reversedItems,
          sections: sections,
          sections_url: window.location.pathname
        })
      });

      const result = await response.json();

      if (result.status === 422) {
        console.error('Bundle Builder: Add to cart error', result.description);
        document.dispatchEvent(new CustomEvent('ajaxProduct:error', {
          detail: { errorMessage: result.description }
        }));
      } else {
        if (this.miniCart) {
          this.miniCart.renderContents(result);
        }

        document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
          detail: { product: result }
        }));

        this.clearBundle();
        
        // If was in edit mode, clear URL parameter
        if (this.editMode) {
          const url = new URL(window.location);
          url.searchParams.delete('edit_bundle');
          window.history.replaceState({}, '', url);
          this.editMode = false;
          this.editBundleId = null;
        }
      }
    } catch (error) {
      console.error('Bundle Builder: Network error', error);
    } finally {
      this.addToCartButton.disabled = false;
      this.addToCartButton.classList.remove('loading');
    }
  }

  clearBundle() {
    this.slots.forEach((slot, index) => {
      if (slot.filled && slot.product?.cardElement) {
        this.resetCardUI(slot.product.cardElement);
      }
      slot.filled = false;
      slot.product = null;
      this.updateSlotUI(index);
    });

    while (this.slots.length > this.defaultSlots) {
      const slot = this.slots.pop();
      if (slot.wrapper) {
        slot.wrapper.remove();
      } else {
        slot.element.remove();
      }
    }

    this.selectedSlotIndex = null;
    
    this.clearStorage();
    
    this.ensureEmptySlotVisible();
    
    this.updateUI();
  }
}

customElements.define('bundle-builder', BundleBuilder);
